import { Link, useMatches } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { HomeIcon } from "../icons";
import { NavigationProgress } from "./navigation-progress";

const noCrumbs: readonly string[] = [];

export interface TopbarProps {
	readonly actions: ReactNode;
}

/**
 * 52px glass bar: breadcrumb from the deepest route's `staticData.crumbs` on the left, icon actions on the right, and a
 * progress bar along its foot while a page loads.
 */
export function Topbar({ actions }: TopbarProps) {
	const crumbs = useMatches({ select: matches => matches.at(-1)?.staticData.crumbs }) ?? noCrumbs;

	return (
		<header className="relative z-10 flex h-topbar shrink-0 items-center border-b border-[rgba(120,100,60,.12)] bg-[rgba(250,246,238,.55)] px-[26px] backdrop-blur-[12px] dark:border-line dark:bg-bg">
			<nav aria-label="Breadcrumb">
				<ol className="flex items-center gap-[5px] text-ctl-lg text-ink-3">
					<li className="flex items-center">
						<Link
							to="/"
							activeOptions={{ exact: true }}
							aria-label="Home"
							className="flex press items-center text-ink-3 hover:text-ink"
						>
							<HomeIcon
								className="h-3.5 w-3.5 fill-none stroke-current stroke-[1.6]"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</Link>
					</li>
					{crumbs.map((crumb, index) => {
						const isLast = index === crumbs.length - 1;
						return (
							<li key={crumb} className="flex items-center gap-[5px]">
								<span aria-hidden="true" className="opacity-40">
									/
								</span>
								<span
									aria-current={isLast ? "page" : undefined}
									className={isLast ? "text-ink-2" : undefined}
								>
									{crumb}
								</span>
							</li>
						);
					})}
				</ol>
			</nav>
			<div className="ml-auto flex items-center gap-2">{actions}</div>
			<NavigationProgress />
		</header>
	);
}
